import { env } from '@src/env';
import React, { Component, ReactNode } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import { store } from '@redux/storeConfig/store';
import * as authSelectors from '@redux/reducers/auth';

// Tipos para el estado del Error Boundary
interface ErrorInfo {
  componentStack: string;
  errorBoundary?: string;
  errorInfo?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  showModal: boolean;
  errorCount: number;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  maxErrors?: number;
  fallback?: (error: Error, errorInfo: ErrorInfo, resetError: () => void) => ReactNode;
}

// Función para generar un ID único para cada error
const generateErrorId = (): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `err_${timestamp}_${random}`;
};

// Función para enviar errores al backend (solo en producción)
const sendErrorToBackend = async (
  error: Error,
  errorInfo: ErrorInfo,
  errorId: string
): Promise<void> => {
  if (!env.PROD) return;

  try {
    // Obtener el usuario desde Redux store
    const state = store.getState();
    const user = authSelectors.getUser(state);
    const userId = user?.user_id || null;

    const errorData = {
      EventId: errorId,
      ErrorMessage: error.message,
      StackTrace: error.stack,
      ComponentStack: errorInfo.componentStack,
      Url: window.location.href,
      UserAgent: navigator.userAgent,
      UserId: userId,
      SessionId: null, // Puedes poner el sessionId real si lo tienes
      Environment: env.ENVIRONMENT || (env.PROD ? 'Production' : 'Development'),
      ReactVersion: import.meta.env.VITE_VERSION || 'unknown',
      Timestamp: new Date().toISOString()
    };

    await fetch(`${env.VITE_API_BASE_URL}/ErrorLog/log-frontend-error`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(errorData)
    });
  } catch (fetchError) {
    console.error('❌ Error enviando error al backend:', fetchError);
  }
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private maxErrors: number;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.maxErrors = props.maxErrors || 5;

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      showModal: false,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Actualiza el estado para mostrar la UI de error
    return {
      hasError: true,
      error: error,
      errorId: generateErrorId()
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorId = this.state.errorId || generateErrorId();
    const newErrorCount = this.state.errorCount + 1;

    console.group('🚨 Error Boundary - Error Capturado');
    console.error('Error: ', error);
    console.error('Error Info:', errorInfo);
    console.error('Timestamp:', new Date().toISOString());
    console.error('Event ID:', errorId);
    console.error('URL:', window.location.href);
    console.error('User Agent:', navigator.userAgent);
    console.groupEnd();

    // Prevenir demasiados errores consecutivos
    if (newErrorCount >= this.maxErrors) {
      console.error(
        `❌ Demasiados errores (${newErrorCount}). Deteniendo captura para evitar loop infinito.`
      );
      return;
    }

    this.setState({
      errorInfo: errorInfo,
      errorId: errorId,
      showModal: env.DEV, // Solo mostrar modal en desarrollo
      errorCount: newErrorCount
    });

    // Enviar error al backend en producción
    sendErrorToBackend(error, errorInfo, errorId);
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      showModal: false
    });
  };

  toggleModal = () => {
    this.setState((prevState) => ({
      showModal: !prevState.showModal
    }));
  };

  reloadPage = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error, errorInfo, errorId, showModal } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      // Si hay un fallback personalizado, usarlo
      if (fallback && errorInfo) {
        try {
          return fallback(error, errorInfo, this.resetError);
        } catch (fallbackError) {
          console.error('❌ Error en fallback personalizado:', fallbackError);
          // Continuar con el fallback por defecto
        }
      }

      // Fallback por defecto
      return (
        <div className="error-boundary">
          {/* Modal para desarrollo */}
          {env.DEV && (
            <Modal isOpen={showModal} toggle={this.toggleModal} size="lg">
              <ModalHeader toggle={this.toggleModal} className="bg-danger text-white">
                🚨 Error de Aplicación - Desarrollo
              </ModalHeader>
              <ModalBody>
                <Alert color="danger">
                  <h6 className="alert-heading">Error Capturado:</h6>
                  <p className="mb-1">
                    <strong>{error.message}</strong>
                  </p>
                  <small>Event ID: {errorId}</small>
                </Alert>

                <div className="mb-3">
                  <h6>Stack Trace:</h6>
                  <pre
                    className="bg-light p-3"
                    style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}
                  >
                    {error.stack}
                  </pre>
                </div>

                {errorInfo && (
                  <div className="mb-3">
                    <h6>Component Stack:</h6>
                    <pre
                      className="bg-light p-3"
                      style={{ fontSize: '12px', overflow: 'auto', maxHeight: '150px' }}
                    >
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}

                <Alert color="info">
                  <small>
                    <strong>Tip:</strong> Este modal solo aparece en desarrollo. En producción, los
                    errores se envían automáticamente al servidor.
                  </small>
                </Alert>
              </ModalBody>
              <ModalFooter>
                <Button color="success" onClick={this.resetError}>
                  Reintentar
                </Button>
                <Button color="warning" onClick={this.reloadPage}>
                  Recargar Página
                </Button>
                <Button color="secondary" onClick={this.toggleModal}>
                  Cerrar
                </Button>
              </ModalFooter>
            </Modal>
          )}

          {/* UI de error principal */}
          <div className="d-flex justify-content-center align-items-center min-vh-100">
            <div className="text-center">
              <div className="error-content">
                <h2 className="mb-3">{env.DEV ? '🛠️ Error de Desarrollo' : '😕 Algo salió mal'}</h2>

                {env.DEV ? (
                  <div>
                    <p className="text-muted mb-3">
                      Se ha producido un error en la aplicación. Revisa la consola y el modal para
                      más detalles.
                    </p>
                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                      <Button color="primary" onClick={this.toggleModal}>
                        Ver Detalles
                      </Button>
                      <Button color="success" onClick={this.resetError}>
                        Reintentar
                      </Button>
                      <Button color="warning" onClick={this.reloadPage}>
                        Recargar Página
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-muted mb-3">
                      Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado.
                    </p>
                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                      <Button color="primary" onClick={this.resetError}>
                        Reintentar
                      </Button>
                      <Button color="secondary" onClick={this.reloadPage}>
                        Recargar Página
                      </Button>
                    </div>
                    {errorId && (
                      <p className="mt-3">
                        <small className="text-muted">ID del error: {errorId}</small>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
